import { useCallback, useEffect, useMemo, useState } from 'react';
import { GraduationCap, Phone, User, Wallet } from 'lucide-react';
import { useDashboardUi } from '@/components/layout/UserLayout';
import { BillIconSelect } from '@/components/bills/BillIconSelect';
import { BillPaymentSummary } from '@/components/bills/BillPaymentSummary';
import { BillPlanPicker } from '@/components/bills/BillPlanPicker';
import { BillValidatedCard } from '@/components/bills/BillValidatedCard';
import { billInputClass, formatBillNaira } from '@/components/bills/billPaymentUtils';
import { BillPurchaseSuccessModal } from '@/components/receipts/BillPurchaseSuccessModal';
import { PinVerificationModal } from '@/components/user/PinVerificationModal';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { useUserAuth } from '@/context/UserAuthContext';
import { getExamProviderColor, getExamProviderLogo } from '@/lib/billPricingAssets';
import { billsService, getApiErrorMessage } from '@/services';
import type { BillPlan, BillProduct, BillPurchaseResult } from '@/types/bills';
import type { BillReceiptData } from '@/types/receipt';
import { cn } from '@/utils/cn';
import { notify } from '@/utils/notify';
import { formatReceiptDateTime, extractExamPins, getExamDeliverable } from '@/utils/receiptFormatters';

function FormLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="mb-2">
      <label className="block text-sm font-medium text-gray-700">{children}</label>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export function ExamsPage() {
  const { user, persistUser } = useUserAuth();
  const { wallet, loadWallet } = useDashboardUi();
  const profilePhone = user?.phone ?? '';

  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);

  const [providers, setProviders] = useState<BillProduct[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [plans, setPlans] = useState<BillPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanCode, setSelectedPlanCode] = useState('');

  const [candidateName, setCandidateName] = useState('');
  const [candidatePhone, setCandidatePhone] = useState('');
  const [validatedName, setValidatedName] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [successModal, setSuccessModal] = useState<BillReceiptData | null>(null);

  const [showPinModal, setShowPinModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  const walletBalance = wallet?.balance ?? user?.walletBalance ?? 0;
  const selectedProvider = useMemo(
    () => providers.find((p) => p.serviceId === selectedProviderId) ?? null,
    [providers, selectedProviderId],
  );
  const selectedPlan = useMemo(
    () => plans.find((p) => p.variationCode === selectedPlanCode) ?? null,
    [plans, selectedPlanCode],
  );

  const providerOptions = useMemo(
    () =>
      providers.map((provider) => ({
        value: provider.serviceId,
        label: provider.fullName,
        sublabel: provider.name.toUpperCase(),
        logoUrl: getExamProviderLogo(provider.serviceId, provider.name),
        fallbackColor: getExamProviderColor(provider.serviceId),
        fallbackInitial: provider.name.toUpperCase().slice(0, 4),
      })),
    [providers],
  );

  const planOptions = useMemo(
    () =>
      plans.map((plan) => ({
        value: plan.variationCode,
        label: plan.name,
        price: plan.price ?? plan.subscriberPrice,
        leadingIcon: <GraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-secondary-500" />,
      })),
    [plans],
  );

  const amountToPay = selectedPlan?.price ?? selectedPlan?.subscriberPrice ?? null;

  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billsService.getExamProviders();
      const data = res.data;
      setProviders(data?.providers ?? []);
      setConfigured(Boolean(data?.configured));
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load exam providers'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPlans = useCallback(async (serviceId: string) => {
    setPlansLoading(true);
    try {
      const res = await billsService.getExamPlans(serviceId);
      const loaded = res.data?.plans ?? [];
      setPlans(loaded);
      setSelectedPlanCode(loaded[0]?.variationCode ?? '');
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load exam plans'));
      setPlans([]);
      setSelectedPlanCode('');
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    if (selectedProviderId) {
      void loadPlans(selectedProviderId);
      setValidatedName(null);
      setCandidateName('');
      setCandidatePhone(profilePhone.replace(/\D/g, '').slice(0, 11));
      setSuccessModal(null);
    } else {
      setPlans([]);
      setSelectedPlanCode('');
    }
  }, [selectedProviderId, loadPlans, profilePhone]);

  useEffect(() => {
    setValidatedName(null);
  }, [candidateName, candidatePhone, selectedPlanCode]);

  async function handleValidateCandidate() {
    if (!selectedProvider || !selectedPlan) {
      notify.error('Select a provider and plan first');
      return;
    }
    if (candidateName.trim().length < 2) {
      notify.error('Enter candidate full name');
      return;
    }
    if (candidatePhone.length < 11) {
      notify.error('Enter a valid candidate phone (11 digits)');
      return;
    }
    setValidating(true);
    try {
      const res = await billsService.validateExamProfile({
        serviceId: selectedProvider.serviceId,
        candidateName: candidateName.trim(),
        phone: candidatePhone,
        variationCode: selectedPlan.variationCode,
      });
      setValidatedName(res.data?.customerName ?? candidateName.trim());
      notify.success('Candidate validated');
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Candidate validation failed'));
    } finally {
      setValidating(false);
    }
  }

  function handlePayClick() {
    if (!validatedName) {
      notify.error('Validate candidate details before purchasing');
      return;
    }
    setShowPinModal(true);
  }

  function buildExamReceipt(data: BillPurchaseResult): BillReceiptData {
    const { date, time } = formatReceiptDateTime();
    const examPins = extractExamPins(data);
    return {
      type: 'exam',
      reference: data.reference,
      amount: data.faceValue,
      amountPaid: data.amountPaid,
      providerName: selectedProvider!.fullName,
      date,
      time,
      planName: selectedPlan!.name,
      candidateName: candidateName.trim(),
      phone: candidatePhone,
      purchasedCode: data.purchasedCode ?? null,
      examPins,
    };
  }

  async function handlePurchase(pin: string) {
    if (!selectedProvider || !selectedPlan || !amountToPay) return;
    setPurchasing(true);
    try {
      const res = await billsService.purchaseExam({
        serviceId: selectedProvider.serviceId,
        variationCode: selectedPlan.variationCode,
        phone: candidatePhone,
        candidateName: candidateName.trim(),
        pin,
      });
      const data = res.data;
      if (!data) return;
      if (data.user) persistUser({ ...user!, walletBalance: data.user.walletBalance });
      await loadWallet();
      setShowPinModal(false);

      const deliverable = getExamDeliverable(data);
      if (deliverable) {
        setSuccessModal(buildExamReceipt(data));
      } else {
        notify.success(data.message ?? 'PIN purchased successfully');
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Purchase failed'));
    } finally {
      setPurchasing(false);
    }
  }

  const canValidate =
    selectedProvider &&
    selectedPlan &&
    candidateName.trim().length >= 2 &&
    candidatePhone.length >= 11 &&
    !validatedName;

  const canPay =
    configured &&
    selectedProvider &&
    selectedPlan &&
    amountToPay != null &&
    walletBalance >= amountToPay &&
    candidatePhone.length >= 11 &&
    candidateName.trim().length >= 2 &&
    Boolean(validatedName);

  const summaryRows = [
    { label: 'Provider', value: selectedProvider?.fullName ?? 'Not selected' },
    { label: 'Plan', value: selectedPlan?.name ?? '—' },
    { label: 'Candidate', value: validatedName || candidateName.trim() || '—' },
    { label: 'Candidate phone', value: candidatePhone || '—' },
  ];

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader label="Loading exam services..." />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 pb-28 md:pb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Exam Result Checker PINs</h1>
        <p className="text-sm text-gray-500">
          Buy WAEC, NECO, or NABTEB result-checker PINs from your wallet.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="overflow-hidden border-gray-200 p-0 shadow-md">
          <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
            <h2 className="font-semibold text-gray-900">Make payment</h2>
          </div>
          <div className="space-y-5 p-5">
            <BillIconSelect
              label="Select exam provider"
              placeholder="Choose a provider"
              sheetTitle="Exam providers"
              value={selectedProviderId}
              options={providerOptions}
              onChange={setSelectedProviderId}
            />

            <div className={cn(!selectedProvider && 'pointer-events-none opacity-50')}>
              <BillPlanPicker
                label="Select plan"
                placeholder={selectedProvider ? 'Choose a plan' : 'Select a provider first'}
                sheetTitle="Exam plans"
                value={selectedPlanCode}
                options={planOptions}
                onChange={setSelectedPlanCode}
                disabled={!selectedProvider}
                loading={plansLoading}
              />
            </div>

            <div className={cn(!selectedPlan && 'pointer-events-none opacity-50')}>
              <FormLabel>Candidate full name</FormLabel>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                  placeholder="Enter candidate full name"
                  className={cn(billInputClass, 'pl-10')}
                />
              </div>
            </div>

            <div className={cn(!selectedPlan && 'pointer-events-none opacity-50')}>
              <FormLabel hint="Required by Otobill — format 08012345678. Prefills from your profile; edit if needed.">
                Candidate phone
              </FormLabel>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    inputMode="numeric"
                    value={candidatePhone}
                    onChange={(e) => setCandidatePhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    placeholder="08012345678"
                    className={cn(billInputClass, 'pl-10')}
                  />
                </div>
                {canValidate && (
                  <Button
                    type="button"
                    size="sm"
                    className="shrink-0 px-4"
                    loading={validating}
                    onClick={() => void handleValidateCandidate()}
                  >
                    Validate
                  </Button>
                )}
              </div>
            </div>

            {validatedName && (
              <BillValidatedCard title="Candidate validated">
                <p className="font-medium">{validatedName}</p>
                <p className="text-xs text-gray-600">{candidatePhone}</p>
              </BillValidatedCard>
            )}

            <Button fullWidth disabled={!canPay} onClick={handlePayClick}>
              <Wallet className="h-4 w-4" />
              Pay {amountToPay ? formatBillNaira(amountToPay) : '—'} · Wallet{' '}
              {formatBillNaira(walletBalance)}
            </Button>
          </div>
        </Card>

        <div className="hidden md:block">
          <Card className="sticky top-24 border-gray-200 p-0 shadow-md">
            <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
              <div className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-brand-600" />
                <div>
                  <h2 className="font-semibold text-gray-900">Payment summary</h2>
                  <p className="text-xs text-gray-500">Review your payment details</p>
                </div>
              </div>
            </div>
            <div className="space-y-4 p-5">
              <BillPaymentSummary
                rows={summaryRows}
                total={amountToPay}
                walletBalance={walletBalance}
                reversalNotice="Please ensure all candidate information is correct. Payments are processed instantly and cannot be reversed."
              />
            </div>
          </Card>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-[4.5rem] z-40 border-t border-gray-200 bg-white/95 p-3 backdrop-blur md:hidden">
        <Button fullWidth disabled={!canPay} onClick={handlePayClick}>
          Pay {amountToPay ? formatBillNaira(amountToPay) : '—'} · Wallet{' '}
          {formatBillNaira(walletBalance)}
        </Button>
      </div>

      <PinVerificationModal
        open={showPinModal}
        onClose={() => setShowPinModal(false)}
        onConfirm={(pin) => void handlePurchase(pin)}
        loading={purchasing}
        title="Confirm exam PIN purchase"
      />

      {successModal && (
        <BillPurchaseSuccessModal
          open
          onClose={() => setSuccessModal(null)}
          receipt={successModal}
        />
      )}
    </div>
  );
}
